/** Theme configuration for storefront embed preview and snippet generation. */

export type EmbedFontPreset = 'inter' | 'system' | 'playfair' | 'georgia';
export type ProductPlaceholderTheme = 'light' | 'dark';

export interface EmbedThemeConfig {
  primary: string;
  background: string;
  surface: string;
  text: string;
  border: string;
  accent: string;
  transparentBackground: boolean;
  borderRadius: number;
  fontSans: EmbedFontPreset;
  fontSerif: EmbedFontPreset;
  baseFontSize: number;
  /** Muted body / label copy */
  textMuted?: string;
  /** De-emphasized copy (strikethrough, hints) */
  textLight?: string;
  /** Secondary headings / form labels */
  textSecondary?: string;
  /** Raised panels within cards */
  surfaceElevated?: string;
  /** Subtle dividers inside cards */
  borderLight?: string;
  /** Primary CTA background (e.g. gold on dark storefronts) */
  buttonBackground?: string;
  /** Primary CTA label color */
  buttonText?: string;
  /** Primary CTA hover background */
  buttonHoverBackground?: string;
  /** Product image well / placeholder backdrop */
  imageBackground?: string;
  /** Success / purity accent */
  success?: string;
}

export interface EmbedConnectionConfig {
  apiBase: string;
  storeId: string;
}

export const DEFAULT_EMBED_THEME: EmbedThemeConfig = {
  primary: '#b4932a',
  background: '#fafaf9',
  surface: '#ffffff',
  text: '#1c1917',
  border: '#e7e5e4',
  accent: '#d4af37',
  transparentBackground: false,
  borderRadius: 4,
  fontSans: 'inter',
  fontSerif: 'playfair',
  baseFontSize: 16,
};

const FONT_STACKS: Record<EmbedFontPreset, { sans: string; serif: string }> = {
  inter: {
    sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    serif: "'Playfair Display', Georgia, serif",
  },
  system: {
    sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    serif: "Georgia, 'Times New Roman', serif",
  },
  playfair: {
    sans: "'Inter', sans-serif",
    serif: "'Playfair Display', Georgia, serif",
  },
  georgia: {
    sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    serif: "Georgia, 'Times New Roman', serif",
  },
};

const LIGHT_SEMANTIC_DEFAULTS = {
  textMuted: '#78716c',
  textLight: '#a8a29e',
  textSecondary: '#44403c',
  surfaceElevated: '#ffffff',
  borderLight: '#f5f5f4',
  buttonBackground: '#1c1917',
  buttonText: '#ffffff',
  buttonHoverBackground: '#b4932a',
  imageBackground: '#fafaf9',
  success: '#22c55e',
} as const;

const DARK_SEMANTIC_DEFAULTS = {
  textMuted: '#9fb2bf',
  textLight: '#7a8f9c',
  textSecondary: '#c5d3dc',
  surfaceElevated: '#122030',
  borderLight: 'rgba(126, 200, 227, 0.08)',
  buttonBackground: '#F5C842',
  buttonText: '#060d14',
  buttonHoverBackground: '#35E6D0',
  imageBackground: '#0a1420',
  success: '#35E6D0',
} as const;

export function resolveFontStacks(preset: EmbedFontPreset): { sans: string; serif: string } {
  return FONT_STACKS[preset] ?? FONT_STACKS.inter;
}

/** True when the page/background token reads as a dark surface. */
export function isDarkBackground(color: string): boolean {
  if (!color || color === 'transparent') {
    return false;
  }
  const hex = extractHexColor(color);
  if (!hex) {
    return false;
  }
  return getLuminance(hex) < 0.35;
}

function extractHexColor(color: string): string | null {
  const trimmed = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return trimmed;
  }
  const rgbMatch = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const r = Number.parseInt(rgbMatch[1] ?? '0', 10);
    const g = Number.parseInt(rgbMatch[2] ?? '0', 10);
    const b = Number.parseInt(rgbMatch[3] ?? '0', 10);
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  }
  return null;
}

/** Resolves semantic tokens not explicitly set on the embed theme config. */
export function resolveSemanticThemeTokens(theme: EmbedThemeConfig): Required<
  Pick<
    EmbedThemeConfig,
    | 'textMuted'
    | 'textLight'
    | 'textSecondary'
    | 'surfaceElevated'
    | 'borderLight'
    | 'buttonBackground'
    | 'buttonText'
    | 'buttonHoverBackground'
    | 'imageBackground'
    | 'success'
  >
> {
  const dark = isDarkBackground(theme.transparentBackground ? theme.surface : theme.background);
  const defaults = dark ? DARK_SEMANTIC_DEFAULTS : LIGHT_SEMANTIC_DEFAULTS;

  return {
    textMuted: theme.textMuted ?? defaults.textMuted,
    textLight: theme.textLight ?? defaults.textLight,
    textSecondary: theme.textSecondary ?? defaults.textSecondary,
    surfaceElevated: theme.surfaceElevated ?? defaults.surfaceElevated,
    borderLight: theme.borderLight ?? defaults.borderLight,
    buttonBackground: theme.buttonBackground ?? (dark ? theme.accent : defaults.buttonBackground),
    buttonText: theme.buttonText ?? (dark ? theme.background : defaults.buttonText),
    buttonHoverBackground:
      theme.buttonHoverBackground ?? (dark ? theme.primary : theme.accent),
    imageBackground: theme.imageBackground ?? (dark ? defaults.imageBackground : theme.background),
    success: theme.success ?? (dark ? theme.primary : defaults.success),
  };
}

/** Maps product placeholder SVG theme from embed background luminance. */
export function resolveProductPlaceholderTheme(theme: EmbedThemeConfig): ProductPlaceholderTheme {
  return isDarkBackground(theme.transparentBackground ? theme.surface : theme.background)
    ? 'dark'
    : 'light';
}

export function themeToCssVars(theme: EmbedThemeConfig): Record<string, string> {
  const sansStack = resolveFontStacks(theme.fontSans);
  const serifStack = resolveFontStacks(theme.fontSerif);
  const bg = theme.transparentBackground ? 'transparent' : theme.background;
  const semantic = resolveSemanticThemeTokens(theme);

  return {
    '--landi-primary': theme.primary,
    '--landi-primary-hover': theme.accent,
    '--landi-primary-light': `${theme.primary}1f`,
    '--landi-background': bg,
    '--landi-surface': theme.surface,
    '--landi-surface-elevated': semantic.surfaceElevated,
    '--landi-text': theme.text,
    '--landi-text-secondary': semantic.textSecondary,
    '--landi-text-muted': semantic.textMuted,
    '--landi-text-light': semantic.textLight,
    '--landi-border': theme.border,
    '--landi-border-light': semantic.borderLight,
    '--landi-button-bg': semantic.buttonBackground,
    '--landi-button-text': semantic.buttonText,
    '--landi-button-hover-bg': semantic.buttonHoverBackground,
    '--landi-image-bg': semantic.imageBackground,
    '--landi-success': semantic.success,
    '--landi-success-bg': `${semantic.success}1a`,
    '--landi-radius': `${theme.borderRadius}px`,
    '--landi-radius-lg': `${Math.min(theme.borderRadius + 8, 24)}px`,
    '--landi-font-sans': sansStack.sans,
    '--landi-font-serif': serifStack.serif,
    fontSize: `${theme.baseFontSize}px`,
  };
}

export function themeVarsToInlineStyle(vars: Record<string, string>): string {
  return Object.entries(vars)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');
}

export function themeToLandiGlobal(theme: EmbedThemeConfig): Record<string, string | number | boolean> {
  const semantic = resolveSemanticThemeTokens(theme);
  return {
    primary: theme.primary,
    background: theme.transparentBackground ? 'transparent' : theme.background,
    surface: theme.surface,
    text: theme.text,
    border: theme.border,
    accent: theme.accent,
    transparentBackground: theme.transparentBackground,
    borderRadius: theme.borderRadius,
    fontSans: theme.fontSans,
    fontSerif: theme.fontSerif,
    baseFontSize: theme.baseFontSize,
    textMuted: semantic.textMuted,
    textLight: semantic.textLight,
    textSecondary: semantic.textSecondary,
    surfaceElevated: semantic.surfaceElevated,
    borderLight: semantic.borderLight,
    buttonBackground: semantic.buttonBackground,
    buttonText: semantic.buttonText,
    buttonHoverBackground: semantic.buttonHoverBackground,
    imageBackground: semantic.imageBackground,
    success: semantic.success,
  };
}

/** Relative luminance for WCAG contrast (sRGB). */
export function getLuminance(hex: string): number {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) {
    return 0;
  }
  const rgb = Number.parseInt(normalized, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const channels = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

export function getContrastRatio(foreground: string, background: string): number {
  const fgHex = extractHexColor(foreground);
  const bgHex = extractHexColor(background);
  if (!fgHex || !bgHex) {
    return 1;
  }
  const l1 = getLuminance(fgHex);
  const l2 = getLuminance(bgHex);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

export function failsWcagAaTextContrast(text: string, background: string): boolean {
  if (background === 'transparent') {
    return false;
  }
  return getContrastRatio(text, background) < 4.5;
}

const OPTIONAL_THEME_STRING_KEYS = [
  'textMuted',
  'textLight',
  'textSecondary',
  'surfaceElevated',
  'borderLight',
  'buttonBackground',
  'buttonText',
  'buttonHoverBackground',
  'imageBackground',
  'success',
] as const;

export function validateThemeConfig(config: unknown): EmbedThemeConfig {
  const base: EmbedThemeConfig = { ...DEFAULT_EMBED_THEME };

  if (!config || typeof config !== 'object') {
    return base;
  }

  const parsed = config as Record<string, unknown>;

  if (typeof parsed.primary === 'string') base.primary = parsed.primary;
  if (typeof parsed.background === 'string') base.background = parsed.background;
  if (typeof parsed.surface === 'string') base.surface = parsed.surface;
  if (typeof parsed.text === 'string') base.text = parsed.text;
  if (typeof parsed.border === 'string') base.border = parsed.border;
  if (typeof parsed.accent === 'string') base.accent = parsed.accent;

  for (const key of OPTIONAL_THEME_STRING_KEYS) {
    if (typeof parsed[key] === 'string') {
      base[key] = parsed[key];
    }
  }

  if (typeof parsed.transparentBackground === 'boolean') {
    base.transparentBackground = parsed.transparentBackground;
  }

  if (typeof parsed.borderRadius === 'number') {
    base.borderRadius = parsed.borderRadius;
  }

  if (typeof parsed.fontSans === 'string' && parsed.fontSans in FONT_STACKS) {
    base.fontSans = parsed.fontSans as EmbedFontPreset;
  }

  if (typeof parsed.fontSerif === 'string' && parsed.fontSerif in FONT_STACKS) {
    base.fontSerif = parsed.fontSerif as EmbedFontPreset;
  }

  if (typeof parsed.baseFontSize === 'number') {
    base.baseFontSize = parsed.baseFontSize;
  }

  return base;
}

export function applyThemeTokens(target: HTMLElement, tokens: EmbedThemeConfig | string | unknown): void {
  if (!target) return;

  let parsedTokens: unknown = tokens;
  if (typeof tokens === 'string') {
    try {
      parsedTokens = JSON.parse(tokens);
    } catch {
      parsedTokens = {};
    }
  }

  const validTheme = validateThemeConfig(parsedTokens);
  const cssVars = themeToCssVars(validTheme);

  for (const [key, value] of Object.entries(cssVars)) {
    target.style.setProperty(key, value);
  }
}

/** Biohacks v1.2 dark reference theme for demo + Landi site patch scripts. */
export const BIOHACKS_V12_EMBED_THEME: EmbedThemeConfig = {
  primary: '#35E6D0',
  background: '#060d14',
  surface: '#0e1a24',
  text: '#F0EDE8',
  border: 'rgba(126,200,227,0.14)',
  accent: '#F5C842',
  transparentBackground: false,
  borderRadius: 4,
  fontSans: 'inter',
  fontSerif: 'playfair',
  baseFontSize: 16,
  textMuted: '#9fb2bf',
  textLight: '#7a8f9c',
  textSecondary: '#c5d3dc',
  surfaceElevated: '#122030',
  borderLight: 'rgba(126,200,227,0.08)',
  buttonBackground: '#F5C842',
  buttonText: '#060d14',
  buttonHoverBackground: '#35E6D0',
  imageBackground: '#0a1420',
  success: '#35E6D0',
};
